import I18n from 'i18n-js'
import * as React from 'react'
import Modal from 'react-modal'
import WCC from 'wcc'
import { GuestModal } from './shared'
import * as Client from '../../lib/shopify_client'
import { detailPageURL, formatAmount, fullTitle as fullProductTitle } from '../../lib/product'

type LineItemProps = {
  item: Client.ICartLine
  updateItemQuantity: (args: {
    lineItemID: string,
    quantity: number,
  }) => Promise<void>,
  removeItem: (args: { lineItemID: string }) => Promise<void>,
  theme?: string,
  ministryNames?: Record<string, string>,
}

const LineItem = ({
  item,
  updateItemQuantity,
  removeItem,
  theme,
  ministryNames,
}: LineItemProps) => {
  const { merchandise, merchandise: { title, price }, quantity } = item
  const image: Client.IImage = merchandise.image || Client.DEFAULT_IMAGE
  const [quantityField, setQuantityField] = React.useState<number>(quantity)
  const isPortal = theme === 'portal'
  let quantitySaveButton: JSX.Element = null

  const customAttributes: Client.IMappedCustomAttributes = {}
  item.attributes.forEach((attr) => {
    customAttributes[attr.key] = attr.value
  })

  const productURL = detailPageURL({
    prefix: customAttributes._sourceURL,
    title,
    productID: merchandise.product.id,
    variantID: merchandise.id,
    accountID: customAttributes._accountID,
  })

  const handleRemoveLineItem = React.useCallback(async () => {
    await removeItem({
      lineItemID: item.id,
    })
  }, [removeItem, item])

  const handleIncrement = React.useCallback(async () => {
    const newQty = quantityField + 1
    setQuantityField(newQty)
    await updateItemQuantity({
      lineItemID: item.id,
      quantity: newQty,
    })
  }, [updateItemQuantity, item, quantityField])

  const handleDecrement = React.useCallback(async () => {
    const newQty = quantityField - 1
    if (newQty <= 0) {
      await removeItem({ lineItemID: item.id })
    } else {
      setQuantityField(newQty)
      await updateItemQuantity({
        lineItemID: item.id,
        quantity: newQty,
      })
    }
  }, [updateItemQuantity, removeItem, item, quantityField])

  const handleSaveLineItemQuantity = React.useCallback(async () => {
    if (isPortal && quantityField <= 0) {
      await removeItem({ lineItemID: item.id })
    } else {
      await updateItemQuantity({
        lineItemID: item.id,
        quantity: quantityField,
      })
    }
  }, [updateItemQuantity, removeItem, item, quantityField, isPortal])

  // Portal gates the "must be > 0 to show Update" rule; legacy shows Update whenever it changed.
  const showUpdateButton = isPortal
    ? (quantityField != quantity && quantityField > 0)
    : (quantityField != quantity)

  if (showUpdateButton) {
    quantitySaveButton = (
      <button className="product-button" onClick={handleSaveLineItemQuantity}>
        Update
      </button>
    )
  }

  const productTitle = fullProductTitle({
    product: merchandise.product,
    variant: merchandise,
    showVariant: false,
  })

  if (isPortal) {
    // Derive eyebrow ministry name from the parent slug of _sourceURL —
    // /ministries/reengage/materials → /ministries/reengage → "Re|engage".
    // Falls back to the stored _ministryName (which may be stale for items
    // added before ministry-name resolution was fixed).
    const parentSlug = (customAttributes._sourceURL || '').replace(/\/[^/]*$/, '')
    const eyebrow = ministryNames?.[parentSlug] || customAttributes._ministryName
    return (
      <div className="cart-line">
        <a href={productURL} className="cart-line__image">
          <img src={image.src} alt={image.altText} />
        </a>
        <div className="cart-line__content">
          <span className="cart-line__eyebrow">
            <a href={customAttributes._sourceURL}>{eyebrow}</a>
          </span>
          <a href={productURL} className="cart-line__title">{productTitle}</a>
        </div>
        <button className="cart-line__remove" onClick={handleRemoveLineItem} aria-label="Remove from cart">
          <span className="material-symbols-outlined">delete</span>
        </button>
        <span className="qty-stepper">
          <button className="qty-stepper__btn" onClick={handleDecrement} aria-label="Decrease quantity">−</button>
          <input
            type="number"
            pattern="[0-9]*"
            onChange={(e) => setQuantityField(e.target.valueAsNumber)}
            onBlur={handleSaveLineItemQuantity}
            onFocus={(e) => e.target.select()}
            value={quantityField}
            min={0}
          />
          <button className="qty-stepper__btn" onClick={handleIncrement} aria-label="Increase quantity">+</button>
        </span>
        <span className="cart-line__price">{formatAmount(price)}</span>
      </div>
    )
  }

  return (
    <div className="row">
      <div className="column medium-12">
        <div className="product-card">
          <div className="product-card__image">
            <img src={image.src} alt={image.altText} />
          </div>
          <div className="product-card__content">
            <h4 className="product-card__ministry-name">
              <a href={customAttributes._sourceURL}>
                {customAttributes._ministryName}
              </a>
            </h4>
            <h3 className="product-card__title">
              <a href={productURL}>{productTitle}</a>
            </h3>
            <span className="product-card__asset-counter">
              <span className="label">Qty:</span>
              <input
                type="number"
                pattern="[0-9]*"
                onChange={(e) => setQuantityField(e.target.valueAsNumber)}
                onFocus={(e) => e.target.select()}
                value={quantityField}
              />
              {quantitySaveButton}
            </span>
            <div className="product-card__actions">
              <button className="product-button" onClick={handleRemoveLineItem}>
                Remove From Cart
              </button>
              <button className="product-button">
                Price <span className="product-button__price">{formatAmount(price)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CartSummary = ({
  cart,
  beforeCheckout,
  theme,
}: {
  cart: Client.ICart,
  beforeCheckout: () => Promise<void>,
  theme?: string,
}) => {
  const { cost: { subtotalAmount: amount }, lines, checkoutUrl } = cart
  const isPortal = theme === 'portal'

  const items: Client.ICartLine[] = lines.edges.map(edge => edge.node)

  const [modalOpen, setModalOpen] = React.useState<boolean>(false)

  const itemCount = items.reduce((sum, node) => {
    return sum + node.quantity
  }, 0)

  const itemCountText = `${itemCount} item${itemCount == 1 ? '' : 's'}`

  const handleCheckout = React.useCallback(async () => {
    if (WCC.currentUser) {
      await beforeCheckout()
      window.location.href = checkoutUrl
    } else {
      setModalOpen(true)
      await beforeCheckout()
    }
  }, [checkoutUrl, beforeCheckout])

  return (
    <div className="row">
      <div className="column medium-12 totals">
        {isPortal ? (
          <>
            <span className="totals__count">{itemCountText}</span>
            <span className="totals__subtotal">Subtotal: {formatAmount(amount)}</span>
          </>
        ) : (
          <table>
            <tbody>
              <tr>
                <th>Subtotal ({itemCountText})</th>
                <td>{formatAmount(amount)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <div className="column medium-12 actions">
        {isPortal && (
          <p className="actions__shipping-notice">{I18n.t('cart.shipping_details')}</p>
        )}
        <a className="button large primary" onClick={handleCheckout}>
          Checkout
        </a>
      </div>
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        className="ReactModal__WMRContent"
        htmlOpenClassName="ReactModal__HTML"
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        contentLabel="Continue As Guest?"
        ariaHideApp={false}
      >
        <GuestModal guestURL={checkoutUrl} signInURL={'/users/sign_in'} />
      </Modal>
    </div>
  )
}

const StorefrontCartAPI: { LineItem: typeof LineItem, CartSummary: typeof CartSummary } = {
  LineItem: LineItem,
  CartSummary: CartSummary
}

export default StorefrontCartAPI
